/**
 * JobExpensesSection — admin-only job expense tracker, embedded under Job Profitability.
 * Expenses (beyond diesel) subtract from Net Revenue. Expense types (Category + optional
 * Sub-category) are reusable and auto-saved. Supports payment type (Check/Amex/Cash) with
 * a Check #, notes, "bill to customer", multiple attachments, and an editable date.
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Paperclip, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { API } from "@/App";

const fmtUSD = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const todayIso = () => new Date().toISOString().slice(0, 10);
const blank = () => ({ id: null, date: todayIso(), category: "", subcategory: "", amount: "", payment_type: "cash", check_number: "", notes: "", bill_to_customer: false, attachments: [], expense_kind: "manual", round_trip_miles: "" });
const ROLLBACK_MPG = 7;

const JobExpensesSection = ({ quoteId, onChanged }) => {
  const [expenses, setExpenses] = useState([]);
  const [types, setTypes] = useState([]);
  const [dieselRate, setDieselRate] = useState(0);
  const [dialog, setDialog] = useState({ open: false, entry: blank(), saving: false });
  const [uploading, setUploading] = useState(false);

  const refresh = useCallback(async () => {
    if (!quoteId) return;
    try {
      const [exRes, tRes, dRes] = await Promise.all([
        axios.get(`${API}/quotes/${quoteId}/expenses`),
        axios.get(`${API}/expense-types`),
        axios.get(`${API}/admin/settings/diesel-price`).catch(() => ({ data: {} })),
      ]);
      setExpenses(exRes.data?.expenses || []);
      setTypes(Array.isArray(tRes.data) ? tRes.data : []);
      setDieselRate(Number(dRes.data?.price_per_gallon) || 0);
    } catch {
      setExpenses([]);
    }
  }, [quoteId]);

  useEffect(() => { refresh(); }, [refresh]);

  const categories = [...new Set(types.map((t) => t.category))];
  const subsFor = (cat) => types.filter((t) => t.category === cat && t.subcategory).map((t) => t.subcategory);

  const setEntry = (patch) => setDialog((p) => ({ ...p, entry: { ...p.entry, ...patch } }));

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        if (file.size > 25 * 1024 * 1024) { toast.error(`${file.name} is over 25MB`); continue; }
        const dataUrl = await new Promise((res, rej) => { const r = new FileReader(); r.onerror = () => rej(r.error); r.onload = () => res(r.result); r.readAsDataURL(file); });
        const up = await axios.post(`${API}/equipment/upload`, { data: dataUrl, filename: file.name, content_type: file.type });
        setDialog((p) => ({ ...p, entry: { ...p.entry, attachments: [...p.entry.attachments, { url: up.data.url, filename: up.data.filename, content_type: up.data.content_type, is_image: up.data.is_image }] } }));
      }
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleSave = async () => {
    const en = dialog.entry;
    const isRollback = en.expense_kind === "rollback_round_trip";
    if (isRollback) {
      if (!en.round_trip_miles || Number(en.round_trip_miles) <= 0) { toast.error("Enter the round trip mileage"); return; }
    } else {
      if (!en.category.trim()) { toast.error("Category is required"); return; }
      if (en.amount === "" || Number(en.amount) < 0) { toast.error("Enter a valid amount"); return; }
    }
    setDialog((p) => ({ ...p, saving: true }));
    const payload = {
      date: en.date || todayIso(),
      payment_type: en.payment_type,
      check_number: en.payment_type === "check" ? (en.check_number.trim() || null) : null,
      notes: en.notes.trim() || null,
      bill_to_customer: !!en.bill_to_customer,
      attachments: en.attachments,
      ...(isRollback
        ? { expense_kind: "rollback_round_trip", round_trip_miles: Number(en.round_trip_miles), category: "Rollback Round Trip" }
        : { category: en.category.trim(), subcategory: en.subcategory.trim() || null, amount: Number(en.amount) }),
    };
    try {
      if (en.id) await axios.put(`${API}/quotes/${quoteId}/expenses/${en.id}`, payload);
      else await axios.post(`${API}/quotes/${quoteId}/expenses`, payload);
      toast.success(en.id ? "Expense updated" : "Expense added");
      setDialog({ open: false, entry: blank(), saving: false });
      refresh();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save expense");
      setDialog((p) => ({ ...p, saving: false }));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this expense?")) return;
    try {
      await axios.delete(`${API}/quotes/${quoteId}/expenses/${id}`);
      refresh();
      onChanged?.();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const openEdit = (ex) => setDialog({
    open: true, saving: false,
    entry: {
      id: ex.id, date: (ex.date || todayIso()).slice(0, 10), category: ex.category || "", subcategory: ex.subcategory || "",
      amount: String(ex.amount ?? ""), payment_type: ex.payment_type || "cash", check_number: ex.check_number || "",
      notes: ex.notes || "", bill_to_customer: !!ex.bill_to_customer, attachments: ex.attachments || [],
      expense_kind: ex.expense_kind || "manual", round_trip_miles: ex.round_trip_miles != null ? String(ex.round_trip_miles) : "",
    },
  });

  return (
    <div className="mt-6 pt-5 border-t border-zinc-200" data-testid="job-expenses-section">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-xs font-semibold text-zinc-700 uppercase tracking-wide">Expenses</h4>
        <Button size="sm" variant="outline" onClick={() => setDialog({ open: true, entry: blank(), saving: false })} className="h-8 px-3 rounded-sm" data-testid="add-expense-btn">
          <Plus className="w-4 h-4 mr-1.5" /> Add Expense
        </Button>
      </div>

      {expenses.length === 0 ? (
        <div className="text-center py-5 border-2 border-dashed border-zinc-200 rounded-md" data-testid="no-expenses">
          <p className="text-sm text-zinc-500">No expenses yet.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {expenses.map((ex) => (
            <div key={ex.id} className="flex items-start gap-3 py-2.5 px-3 bg-zinc-50 rounded-md" data-testid={`expense-row-${ex.id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-zinc-800">
                  {ex.category}{ex.subcategory ? ` · ${ex.subcategory}` : ""}
                  <span className="ml-2 text-[#00341d] font-bold">{fmtUSD(ex.amount)}</span>
                  {ex.bill_to_customer && <span className="ml-2 text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">Bill to customer</span>}
                </div>
                <div className="text-xs text-zinc-500 mt-0.5 flex flex-wrap gap-x-3">
                  <span>{new Date(ex.date).toLocaleDateString()}</span>
                  {ex.payment_type && <span className="capitalize">{ex.payment_type}{ex.check_number ? ` #${ex.check_number}` : ""}</span>}
                  {ex.expense_kind === "rollback_round_trip" && ex.round_trip_miles != null && (
                    <span>{ex.round_trip_miles} mi ÷ {ex.mpg || 7} mpg{ex.diesel_rate ? ` × ${fmtUSD(ex.diesel_rate)}/gal` : ""}</span>
                  )}
                  {ex.attachments?.length > 0 && <span className="flex items-center gap-1"><Paperclip className="w-3 h-3" />{ex.attachments.length}</span>}
                </div>
                {ex.notes && <div className="text-xs text-zinc-500 mt-1 whitespace-pre-wrap">{ex.notes}</div>}
                {ex.attachments?.length > 0 && (
                  <div className="flex gap-2 mt-2 flex-wrap">
                    {ex.attachments.map((a, i) => (
                      <a key={a.url || `${ex.id}-${i}`} href={a.url} target="_blank" rel="noopener noreferrer" className="text-xs text-[#00341d] underline" data-testid={`expense-attachment-${ex.id}-${i}`}>
                        {a.is_image ? <img src={a.url} alt={a.filename} className="w-12 h-12 object-cover rounded border border-zinc-200" /> : (a.filename || "file")}
                      </a>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button type="button" onClick={() => openEdit(ex)} className="p-1.5 rounded hover:bg-zinc-200 text-zinc-600" data-testid={`edit-expense-${ex.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                <button type="button" onClick={() => handleDelete(ex.id)} className="p-1.5 rounded hover:bg-red-100 text-zinc-600 hover:text-red-600" data-testid={`delete-expense-${ex.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={dialog.open} onOpenChange={(open) => !dialog.saving && setDialog((p) => ({ ...p, open }))}>
        <DialogContent className="max-w-md" data-testid="expense-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-tight text-[#00341d]">{dialog.entry.id ? "Edit Expense" : "Add Expense"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2 max-h-[65vh] overflow-y-auto">
            <div>
              <Label>Type</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                <button type="button" onClick={() => setEntry({ expense_kind: "manual" })}
                  className={`h-9 rounded-md border text-sm font-medium ${dialog.entry.expense_kind !== "rollback_round_trip" ? "bg-[#00341d] text-white border-[#00341d]" : "border-zinc-200 text-zinc-600"}`}
                  data-testid="expense-kind-manual">Expense</button>
                <button type="button" onClick={() => setEntry({ expense_kind: "rollback_round_trip" })}
                  className={`h-9 rounded-md border text-sm font-medium ${dialog.entry.expense_kind === "rollback_round_trip" ? "bg-[#00341d] text-white border-[#00341d]" : "border-zinc-200 text-zinc-600"}`}
                  data-testid="expense-kind-rollback">Rollback Round Trip</button>
              </div>
            </div>
            <div>
              <Label>Date</Label>
              <Input type="date" value={dialog.entry.date} onChange={(e) => setEntry({ date: e.target.value })} data-testid="expense-date" />
            </div>
            {dialog.entry.expense_kind === "rollback_round_trip" ? (
              <div>
                <Label>Round trip mileage *</Label>
                <Input type="number" step="1" min="0" inputMode="decimal" value={dialog.entry.round_trip_miles} onChange={(e) => setEntry({ round_trip_miles: e.target.value })} placeholder="e.g. 140" data-testid="expense-round-trip-miles" />
                <p className="text-xs text-zinc-500 mt-1.5" data-testid="rollback-cost-preview">
                  {Number(dialog.entry.round_trip_miles) > 0 && dieselRate > 0
                    ? `${dialog.entry.round_trip_miles} mi ÷ ${ROLLBACK_MPG} mpg × ${fmtUSD(dieselRate)}/gal = `
                    : `Calculated at ${ROLLBACK_MPG} mpg using the current off-road diesel price (${fmtUSD(dieselRate)}/gal). `}
                  {Number(dialog.entry.round_trip_miles) > 0 && dieselRate > 0 && (
                    <span className="font-bold text-[#00341d]">{fmtUSD((Number(dialog.entry.round_trip_miles) / ROLLBACK_MPG) * dieselRate)}</span>
                  )}
                </p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Category *</Label>
                    <Input list="expense-categories" value={dialog.entry.category} onChange={(e) => setEntry({ category: e.target.value })} placeholder="e.g. Subcontractor" data-testid="expense-category" />
                    <datalist id="expense-categories">{categories.map((c) => <option key={c} value={c} />)}</datalist>
                  </div>
                  <div>
                    <Label>Sub-category</Label>
                    <Input list="expense-subs" value={dialog.entry.subcategory} onChange={(e) => setEntry({ subcategory: e.target.value })} placeholder="optional" data-testid="expense-subcategory" />
                    <datalist id="expense-subs">{subsFor(dialog.entry.category).map((s) => <option key={s} value={s} />)}</datalist>
                  </div>
                </div>
                <div>
                  <Label>Amount ($) *</Label>
                  <Input type="number" step="0.01" min="0" inputMode="decimal" value={dialog.entry.amount} onChange={(e) => setEntry({ amount: e.target.value })} placeholder="0.00" data-testid="expense-amount" />
                </div>
              </>
            )}
            <div>
              <Label>Payment type</Label>
              <select value={dialog.entry.payment_type} onChange={(e) => setEntry({ payment_type: e.target.value })} className="w-full h-10 rounded-md border border-zinc-200 px-3 text-sm" data-testid="expense-payment-type">
                <option value="cash">Cash</option>
                <option value="amex">Amex</option>
                <option value="check">Check</option>
              </select>
            </div>
            {dialog.entry.payment_type === "check" && (
              <div>
                <Label>Check # (optional)</Label>
                <Input value={dialog.entry.check_number} onChange={(e) => setEntry({ check_number: e.target.value })} placeholder="e.g. 1042" data-testid="expense-check-number" />
              </div>
            )}
            <label className="flex items-center gap-2 text-sm text-zinc-700 cursor-pointer">
              <input type="checkbox" checked={dialog.entry.bill_to_customer} onChange={(e) => setEntry({ bill_to_customer: e.target.checked })} className="rounded border-zinc-300 accent-[#00341d]" data-testid="expense-bill-to-customer" />
              <span>Bill to customer</span>
            </label>
            <div>
              <Label>Notes</Label>
              <Textarea rows={2} value={dialog.entry.notes} onChange={(e) => setEntry({ notes: e.target.value })} data-testid="expense-notes" />
            </div>
            <div>
              <Label>Attachments (files / photos)</Label>
              <Input type="file" multiple accept="image/*,application/pdf" onChange={handleFiles} disabled={uploading} data-testid="expense-attachments" />
              {uploading && <p className="text-xs text-zinc-400 mt-1">Uploading…</p>}
              {dialog.entry.attachments.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {dialog.entry.attachments.map((a, i) => (
                    <div key={a.url || a.filename || i} className="flex items-center gap-1 text-xs bg-zinc-100 rounded px-2 py-1">
                      <span className="max-w-[120px] truncate">{a.filename || "file"}</span>
                      <button type="button" onClick={() => setEntry({ attachments: dialog.entry.attachments.filter((_, j) => j !== i) })}><X className="w-3 h-3 text-zinc-500" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog({ open: false, entry: blank(), saving: false })} disabled={dialog.saving} className="rounded-sm">Cancel</Button>
            <Button onClick={handleSave} disabled={dialog.saving || uploading} className="bg-[#00341d] hover:bg-[#002816] text-white rounded-sm" data-testid="save-expense-btn">
              {dialog.saving ? "Saving…" : (dialog.entry.id ? "Update" : "Add Expense")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default JobExpensesSection;
